import type { ReactNode } from 'react';

/**
 * The icon set. Line icons on a 24 grid, 1.75 stroke, so they sit next to
 * Archivo at small sizes without going muddy.
 */

const SHAPES: Record<string, ReactNode> = {
  grid: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </>
  ),
  chart: (
    <>
      <path d="M3.5 20.5h17" />
      <path d="M7 20.5v-6" />
      <path d="M12 20.5V6" />
      <path d="M17 20.5v-9" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5.5v13" />
      <path d="M5.5 12h13" />
    </>
  ),
  bookmark: <path d="M18.5 20.5l-6.5-4.5-6.5 4.5V5a1.5 1.5 0 011.5-1.5h10A1.5 1.5 0 0118.5 5z" />,
  sliders: (
    <>
      <path d="M4 21v-6.5" />
      <path d="M4 10.5V3" />
      <path d="M12 21v-9.5" />
      <path d="M12 7.5V3" />
      <path d="M20 21v-4.5" />
      <path d="M20 12.5V3" />
      <path d="M1.5 14.5h5" />
      <path d="M9.5 7.5h5" />
      <path d="M17.5 16.5h5" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5L21 21" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>
  ),
  chevronLeft: <path d="M15 4.5L7.5 12l7.5 7.5" />,
  chevronRight: <path d="M9 4.5l7.5 7.5L9 19.5" />,
  chevronDown: <path d="M5 9l7 7 7-7" />,
  check: <path d="M4.5 12.5l5 5 10-11" />,
  dice: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <circle cx="8.75" cy="8.75" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.35" fill="currentColor" stroke="none" />
      <circle cx="15.25" cy="15.25" r="1.35" fill="currentColor" stroke="none" />
    </>
  ),
  filter: (
    <>
      <path d="M3.5 6.5h17" />
      <path d="M6.5 12h11" />
      <path d="M10 17.5h4" />
    </>
  ),
  sort: (
    <>
      <path d="M7 4v16" />
      <path d="M3.5 16.5L7 20l3.5-3.5" />
      <path d="M13.5 6.5h7" />
      <path d="M13.5 12h5" />
      <path d="M13.5 17.5h3" />
    </>
  ),
  print: (
    <>
      {/* The sheet going in, the machine, the sheet coming out. */}
      <path d="M7 8.5V3.5h10v5" />
      <path d="M7 17.5H5a1.5 1.5 0 01-1.5-1.5v-5A1.5 1.5 0 015 9.5h14a1.5 1.5 0 011.5 1.5v5a1.5 1.5 0 01-1.5 1.5h-2" />
      <path d="M7 14.5h10v6H7z" />
    </>
  ),
  trash: (
    <>
      <path d="M3.5 6.5h17" />
      <path d="M9 6.5V4.75A1.25 1.25 0 0110.25 3.5h3.5A1.25 1.25 0 0115 4.75V6.5" />
      <path d="M6 6.5l.9 13.1a1.5 1.5 0 001.5 1.4h7.2a1.5 1.5 0 001.5-1.4L18 6.5" />
    </>
  ),
  pencil: (
    <>
      <path d="M4 20l4.2-1.1L19.6 7.5a2 2 0 00-2.9-2.8L5.2 16.1z" />
      <path d="M15.5 6.5l2.8 2.8" />
    </>
  ),
  handoff: (
    <>
      <path d="M14 3.5h6.5V10" />
      <path d="M20.5 3.5L12 12" />
      <path d="M20.5 14v5a1.5 1.5 0 01-1.5 1.5H5A1.5 1.5 0 013.5 19V5A1.5 1.5 0 015 3.5h5" />
    </>
  ),
  star: (
    <path d="M12 3.5l2.7 5.6 6.1.85-4.4 4.3 1.05 6.1L12 17.5l-5.45 2.85L7.6 14.25 3.2 9.95l6.1-.85z" />
  ),
  download: (
    <>
      <path d="M12 3.5v11.5" />
      <path d="M7.5 10.5L12 15l4.5-4.5" />
      <path d="M4 20.5h16" />
    </>
  ),
  upload: (
    <>
      <path d="M12 20.5V9" />
      <path d="M7.5 13.5L12 9l4.5 4.5" />
      <path d="M4 3.5h16" />
    </>
  ),
  copy: (
    <>
      <rect x="8.5" y="8.5" width="12" height="12" rx="2" />
      <path d="M15.5 5.5A2 2 0 0013.5 3.5h-8a2 2 0 00-2 2v8a2 2 0 002 2" />
    </>
  ),
  refresh: (
    <>
      <path d="M20.5 12a8.5 8.5 0 11-2.6-6.1" />
      <path d="M20.5 3.5V9h-5.5" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
      <path d="M8 10.5V7.75a4 4 0 018 0V10.5" />
    </>
  ),
  disc: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.3l3.2 2" />
    </>
  ),
  arrowLeft: (
    <>
      <path d="M20 12H4" />
      <path d="M10 6l-6 6 6 6" />
    </>
  ),
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
};

export type IconName = keyof typeof SHAPES;

export interface IconProps {
  name: IconName;
  size?: number;
  /** Fills the shape instead of stroking it — used for a chosen star. */
  filled?: boolean;
  className?: string;
}

export function Icon({ name, size = 20, filled = false, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {SHAPES[name]}
    </svg>
  );
}

/** Google's mark, kept in its own colours as the brand guidelines require. */
export function GoogleMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 009 18z"
      />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 010-3.44V4.96H.96a9 9 0 000 8.08l3.01-2.32z" />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 00.96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}
